import { useCallback, useEffect, useRef } from "react";

// Pauses decoration that loops forever while it is offscreen.
//
//   const ambientRef = useAmbient();
//   <Box ref={ambientRef}>...floating bubbles, pulses, halos...</Box>
//
// The element gets `data-ambient="running" | "paused"`, and the global rules in
// Theme.js (MuiCssBaseline) pause every CSS animation beneath a paused root and
// drop them entirely under prefers-reduced-motion. The attribute is written to
// the DOM directly, never through React state, so scrolling past a section
// costs no re-render.
//
// JS-driven loops (a carousel interval, a frame-swap) pass `onChange` to hear
// visibility flips instead.
//
// One IntersectionObserver is shared by every caller with the same margin.

const observers = new Map(); // rootMargin -> { observer, listeners: Map<Element, fn> }

const getObserver = (rootMargin) => {
  let entry = observers.get(rootMargin);
  if (!entry) {
    const listeners = new Map();
    const observer = new IntersectionObserver(
      (records) => {
        records.forEach((record) => {
          listeners.get(record.target)?.(record.isIntersecting);
        });
      },
      { rootMargin }
    );
    entry = { observer, listeners };
    observers.set(rootMargin, entry);
  }
  return entry;
};

export const useAmbient = ({ rootMargin = "100px", onChange } = {}) => {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const nodeRef = useRef(null);

  return useCallback(
    (node) => {
      const previous = nodeRef.current;
      if (previous && typeof IntersectionObserver !== "undefined") {
        const { observer, listeners } = getObserver(rootMargin);
        observer.unobserve(previous);
        listeners.delete(previous);
      }
      nodeRef.current = node;
      if (!node) return;

      // No observer (old browser, test environment): leave everything running.
      node.setAttribute("data-ambient", "running");
      if (typeof IntersectionObserver === "undefined") {
        onChangeRef.current?.(true);
        return;
      }

      const { observer, listeners } = getObserver(rootMargin);
      listeners.set(node, (inView) => {
        node.setAttribute("data-ambient", inView ? "running" : "paused");
        onChangeRef.current?.(inView);
      });
      observer.observe(node);
    },
    [rootMargin]
  );
};
